import type {
  DashboardActivityItem,
  DashboardApiResponse,
  DashboardData,
  DashboardFunnelStage,
  DashboardMetric,
  DashboardRecentProcess,
  PastaStatus,
} from '../../shared/models';
import {
  PASTA_STATUS_DESCRIPTIONS,
  PASTA_STATUS_LABELS,
  PASTA_STATUS_ORDER,
  apiStatusToPastaStatus,
} from '../../shared/habitaStatusPresentation';
import { HttpRepository } from './HttpRepository';
import { getHabitaDashboardInfoUrl } from './habitaApiUrls';

export interface DashboardFetchParams {
  startDate?: string;
  endDate?: string;
  responsibleEmail?: string;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function numberValue(value: unknown): number {
  const normalizedValue = typeof value === 'string'
    ? value.trim().replace(/%$/, '').replace(',', '.')
    : value;
  const numeric = Number(normalizedValue);
  return Number.isFinite(numeric) ? numeric : 0;
}

function parseJsonLike(value: unknown): unknown {
  if (typeof value !== 'string') return value;

  const trimmed = value.trim();
  if (!trimmed || !['{', '['].includes(trimmed[0])) return value;

  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
}

function arrayValue<T = unknown>(value: unknown): T[] {
  return Array.isArray(value) ? value as T[] : [];
}

function percent(count: number, total: number): number {
  return Math.round((count / Math.max(total, 1)) * 100);
}

function percentMetricValue(value: unknown): number {
  const numeric = numberValue(value);
  if (numeric <= 0) return 0;

  const asPercent = numeric <= 1 ? numeric * 100 : numeric;
  return Math.round(asPercent);
}

function formatPercentMetric(value: unknown): string {
  return `${percentMetricValue(value)}%`;
}

function normalizeStatusKey(value: unknown): string {
  return String(value ?? '').trim().toUpperCase();
}

const processStatusKeys: Record<PastaStatus, string[]> = {
  NOVO: ['NOVO'],
  COLETA_DADOS: ['COLETA_DADOS'],
  RECEBIMENTO_DOCS: ['RECEBIMENTO_DOCS'],
  COLETA_DADOS_COMPLEMENTARES: ['COLETA_DADOS_COMPLEMENTARES'],
  VALIDACAO_PASTA: ['VALIDACAO_PASTA'],
  AJUSTE: ['AJUSTE'],
  PRE_ANALISE_CREDITO: ['PRE_ANALISE_CREDITO'],
  APROVADO: ['APROVADO'],
  REPROVADO: ['REPROVADO'],
  CONDICIONADO: ['CONDICIONADO'],
  desconhecido: [],
};

function readStatusCount(processInfo: Record<string, unknown>, status: PastaStatus): number | undefined {
  const statusCounts = processInfo.status_counts;

  const statusCountRecord = asRecord(statusCounts);
  if (!statusCountRecord) return undefined;

  const statusKeys = new Set([
    status,
    ...processStatusKeys[status],
  ].map(normalizeStatusKey));

  for (const [key, value] of Object.entries(statusCountRecord)) {
    if (statusKeys.has(normalizeStatusKey(key))) {
      return numberValue(value);
    }
  }

  return undefined;
}

function metric(
  id: string,
  label: string,
  value: number | string,
  description: string,
  icon: string,
  tone: DashboardMetric['tone']
): DashboardMetric {
  return {
    id,
    label,
    value: String(value),
    description,
    icon,
    tone,
  };
}

function normalizeDashboardPayload(raw: unknown): DashboardApiResponse {
  const parsed = parseJsonLike(raw);
  if (parsed === null || parsed === undefined || parsed === '') {
    throw new Error('Contrato inválido do dashboard: resposta vazia.');
  }

  const record = asRecord(parsed);
  const recentProcesses = arrayValue(record?.recent_processes);

  if (!record || !('total_count' in record)) {
    const receivedShape = record ? Object.keys(record).slice(0, 10).join(', ') : typeof parsed;
    throw new Error(
      `Contrato inválido do dashboard: a resposta deve conter weekly_info e process_info (recebido: ${receivedShape}).`
    );
  }

  const completedCount = numberValue(record.completed_extraction_count);
  const totalFilesCount = numberValue(record.total_files_received);

  return {
      total_count: numberValue(record.total_count),
      on_going_count: numberValue(record.on_going_count),
      adjustment_count: numberValue(record.adjustment_count),
      approved_count: numberValue(record.approved_count),
      in_validation_count: numberValue(record.in_validation_count),
      manual_reviews_count: numberValue(record.manual_reviews_count),
      completed_extraction_count: completedCount,
      total_files_received: totalFilesCount,
      ai_extraction_failed_count: numberValue(record.ai_extraction_failed_count),
      ai_rejected_count: numberValue(record.ai_rejected_count),
      ai_assertiveness_rate: totalFilesCount > 0 ? percentMetricValue(completedCount / totalFilesCount) : 0,
      recent_processes: arrayValue<DashboardRecentProcess>(recentProcesses),
      status_counts: asRecord(record.status_counts) as Record<string, number> ?? {},
      start_date: record.start_date ? String(record.start_date) : null,
      end_date: record.end_date ? String(record.end_date) : null,
  };
}


function buildApiMetrics(api: DashboardApiResponse): DashboardMetric[] {
  const processInfo = api ?? {};

  return [
    metric(
      'total-clientes',
      'Total de Clientes',
      numberValue(processInfo.total_count),
      'Clientes com pasta documental cadastrada',
      'ri-folder-3-line',
      'info'
    ),
    metric(
      'pastas-em-andamento',
      'Pastas em Andamento',
        numberValue(processInfo.on_going_count),
      'Pastas ainda em tratamento documental',
      'ri-time-line',
      'warning'
    ),
    metric(
      'ajuste-documental',
      'Ajuste Documental',
        numberValue(processInfo.adjustment_count),
      'Pastas aguardando correção ou reenvio',
      'ri-file-warning-line',
      'warning'
    ),
    metric(
      'aprovadas',
      'Aprovadas',
        numberValue(processInfo.approved_count),
      'Pastas aprovadas na análise documental',
      'ri-checkbox-circle-line',
      'success'
    ),
    metric(
      'em-validacao',
      'Em Validação',
        numberValue(processInfo.in_validation_count),
      'Pastas em análise documental',
      'ri-search-eye-line',
      'info'
    ),
    metric(
      'revisoes-manuais',
      'Revisões Manuais',
        numberValue(processInfo.manual_reviews_count),
      'Documentos com intervenção humana',
      'ri-user-settings-line',
      'neutral'
    ),
    metric(
      'extracoes-concluidas',
      'Extrações Concluídas',
      numberValue(processInfo.completed_extraction_count),
      'Documentos processados com sucesso',
      'ri-file-check-line',
      'success'
    ),
    metric(
      'taxa-assertividade',
      'Taxa de Assertividade',
      formatPercentMetric(processInfo.ai_assertiveness_rate),
      'Aprovações da IA sem ajuste manual',
      'ri-sparkling-line',
      'brand'
    ),
  ];
}

function buildApiFunnel(api: DashboardApiResponse): DashboardFunnelStage[] {
  const processInfo = api ?? {};
  const processInfoRecord = processInfo as unknown as Record<string, unknown>;
  const explicitStageCounts = PASTA_STATUS_ORDER.reduce<Partial<Record<PastaStatus, number>>>((acc, status) => {
    const count = readStatusCount(processInfoRecord, status);
    if (count !== undefined) {
      acc[status] = count;
    }
    return acc;
  }, {});
  const statusCountsTotal = Object.values(explicitStageCounts).reduce(
    (sum, count) => sum + numberValue(count),
    0
  );
  const total = numberValue(processInfo.total_count) || statusCountsTotal;

  return PASTA_STATUS_ORDER.map((status) => {
    const count = explicitStageCounts[status] ?? 0;
    return {
      status,
      label: PASTA_STATUS_LABELS[status],
      count,
      percentage: percent(count, total),
      description: PASTA_STATUS_DESCRIPTIONS[status],
    };
  });
}

function optionalText(value: unknown): string | null {
  const text = String(value ?? '').trim();
  return text && text !== '-' ? text : null;
}

function timestampOf(value: string): number {
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function recentProcessStatusUpdatedAt(item: DashboardRecentProcess): string {
  return optionalText(item.last_status_update_at) ?? optionalText(item.updated_at) ?? optionalText(item.created_at) ?? '';
}

function responsibleLabelOf(item: Pick<DashboardRecentProcess, 'created_by' | 'responsible'>): string {
  return optionalText(item.responsible) ?? optionalText(item.created_by) ?? '';
}

function buildActivityFromRecentProcess(item: DashboardRecentProcess): DashboardActivityItem {
  const baseId = String(item.id);
  const status = apiStatusToPastaStatus(item.status);
  const message =
    status === 'APROVADO'
      ? 'Processo aprovado'
      : status === 'REPROVADO'
        ? 'Processo com falha'
        : 'Processo atualizado';

  return {
    id: `process-${baseId}`,
    clienteId: baseId,
    clienteNome: optionalText(item.customer_name) ?? optionalText(item.customer) ?? optionalText(item.name) ?? `Processo #${baseId}`,
    cpf: optionalText(item.customer_cpf) ?? '-',
    responsavel: responsibleLabelOf(item) || '-',
    crmContactId: optionalText(item.crm_contact_id),
    chatwootContactId: optionalText(item.chatwoot_contact_id),
    status,
    updatedAt: recentProcessStatusUpdatedAt(item),
    message,
    href: `/habita/cliente/${encodeURIComponent(baseId)}`,
  };
}

function mapDashboardApiResponse(api: DashboardApiResponse): DashboardData {
  const processInfo = api ?? {};
  const startDate = api.start_date || '';
  const endDate = api.end_date || '';
  const totalWeekly = numberValue(api.total_count);

  return {
    generatedAt: endDate,
    clientes: [],
    metrics: buildApiMetrics(api),
    funnel: buildApiFunnel(api),
    recentActivities: arrayValue<DashboardRecentProcess>(api.recent_processes)
      .map(buildActivityFromRecentProcess)
      .sort((left, right) => timestampOf(right.updatedAt) - timestampOf(left.updatedAt))
      .slice(0, 6),
    usesServerAggregates: true,
    weeklySummary: {
      startDate,
      endDate,
      newFolders: totalWeekly,
      completedExtractions: numberValue(processInfo.completed_extraction_count),
      manualReviews: numberValue(processInfo.manual_reviews_count),
      approvalRate: percentMetricValue(processInfo.ai_assertiveness_rate),
    },
  };
}

export class DashboardRepository {
  private httpRepository = new HttpRepository();

  async fetchDashboard(params: DashboardFetchParams = {}): Promise<DashboardData> {
    return this.fetchApiDashboard(params);
  }

  private async fetchApiDashboard(params: DashboardFetchParams = {}): Promise<DashboardData> {
    const search = new URLSearchParams();
    if (params.startDate) search.set('startDate', params.startDate);
    if (params.endDate) search.set('endDate', params.endDate);
    if (params.responsibleEmail) search.set('responsible_email', params.responsibleEmail);

    const query = search.toString();
    const response = await this.httpRepository.get<unknown>(
      `${getHabitaDashboardInfoUrl()}${query ? `?${query}` : ''}`
    );
    const dashboardData = mapDashboardApiResponse(normalizeDashboardPayload(response.data));

    try {

      return {
        ...dashboardData,
        recentActivities: dashboardData.recentActivities
      };
    } catch (error) {
      console.error('Erro ao carregar últimos contratos com alteração de status:', error);
      return dashboardData;
    }
  }
}
